import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../api";
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  SectionNav,
  Shell,
  SignOutControl,
  StatusPill,
} from "../components";
import { RecoveryEmailCheckoutDialog } from "../components/RecoveryEmailCheckoutDialog";
import {
  createCheemsPayOrder,
  isFirstPurchaseAccount,
  isRefundRequestAvailable,
  refundPolicyConsentText,
  requiresRefundPolicyConsent,
} from "../checkout";
import { formatTotpEnrollmentUri } from "../totp-uri";
import { setPageMetadata } from "../page-metadata";
import { SupportSummary } from "../components/SupportSummary";
import { RedemptionCodeForm } from "../components/RedemptionCodes";
import type {
  AccountResponse,
  CommunityResponse,
  Offer,
  ReferralResponse,
  SessionResponse,
} from "../types";

function readTotpSecret(totpUri: string): string {
  try {
    return new URL(totpUri).searchParams.get("secret") ?? "";
  } catch {
    return "";
  }
}

const REFUND_REASON_OPTIONS = [
  { value: "CHANGED_MIND", label: "改变主意或误购" },
  { value: "NOT_AS_DESCRIBED", label: "商品与描述不符" },
  { value: "DUPLICATE_PURCHASE", label: "重复付款" },
  { value: "CANNOT_USE", label: "商品无法正常使用" },
  { value: "OTHER", label: "其他原因" },
] as const;

type RefundReasonCode = (typeof REFUND_REASON_OPTIONS)[number]["value"];
type AccountSection = "overview" | "orders" | "devices" | "security" | "support";

const ACCOUNT_SECTIONS: ReadonlySet<AccountSection> = new Set([
  "overview",
  "orders",
  "devices",
  "security",
  "support",
]);

const ORDER_STATUS_LABELS: Record<string, string> = {
  CREATED: "订单已创建",
  PENDING_PAYMENT: "等待付款",
  PAID: "付款已确认",
  FULFILLED: "权益已生效",
  CANCELLED: "已取消",
  REFUND_REQUESTED: "退款审核中",
  REFUNDED: "已退款",
  MANUAL_REVIEW: "人工处理中",
};

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback;
}

function isSessionExpired(caught: unknown): boolean {
  return caught instanceof ApiRequestError && caught.status === 401;
}

function redirectToLogin(): void {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.assign(`/?returnTo=${encodeURIComponent(returnTo)}`);
}

function readAccountSection(): AccountSection {
  const section = window.location.hash.slice(1);
  return ACCOUNT_SECTIONS.has(section as AccountSection)
    ? (section as AccountSection)
    : "overview";
}

export function AccountPage() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [account, setAccount] = useState<AccountResponse["account"] | null>(
    null,
  );
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountLoadError, setAccountLoadError] = useState("");
  const [offers, setOffers] = useState<Offer[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [referral, setReferral] = useState<ReferralResponse["referral"] | null>(
    null,
  );
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralLoadError, setReferralLoadError] = useState("");
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [referralBusy, setReferralBusy] = useState(false);
  const [referralError, setReferralError] = useState("");
  const [referralCopied, setReferralCopied] = useState(false);
  const [community, setCommunity] = useState<CommunityResponse["community"]>(
    null,
  );
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityError, setCommunityError] = useState("");
  const [communityGroupCopied, setCommunityGroupCopied] = useState(false);
  const [communityCopied, setCommunityCopied] = useState(false);
  const [error, setError] = useState("");
  const [busyProduct, setBusyProduct] = useState("");
  const [acceptedRefundPolicies, setAcceptedRefundPolicies] = useState<
    Record<string, boolean>
  >({});
  const [busyRefund, setBusyRefund] = useState("");
  const [refundDialogOrderId, setRefundDialogOrderId] = useState("");
  const [refundReasonCode, setRefundReasonCode] = useState<
    RefundReasonCode | ""
  >("");
  const [refundReasonDetail, setRefundReasonDetail] = useState("");
  const [refundReasonError, setRefundReasonError] = useState("");
  const [verificationQueued, setVerificationQueued] = useState(false);
  const [checkoutProductId, setCheckoutProductId] = useState("");
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [checkoutReferralCode, setCheckoutReferralCode] = useState("");
  const [bindingEmail, setBindingEmail] = useState("");
  const [bindingBusy, setBindingBusy] = useState(false);
  const [twoFactorPassword, setTwoFactorPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorUri, setTwoFactorUri] = useState("");
  const [twoFactorQr, setTwoFactorQr] = useState("");
  const [twoFactorSecret, setTwoFactorSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState("");
  const [twoFactorActivated, setTwoFactorActivated] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);
  const [activeSection, setActiveSection] = useState<AccountSection>(
    readAccountSection,
  );
  const [deviceDialogId, setDeviceDialogId] = useState("");
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [deviceError, setDeviceError] = useState("");
  const [deviceSuccess, setDeviceSuccess] = useState("");
  const [accountNotice, setAccountNotice] = useState("");
  const [refundReviewConfirmed, setRefundReviewConfirmed] = useState(false);
  const [twoFactorDisableConfirmed, setTwoFactorDisableConfirmed] =
    useState(false);

  const handleSupplementaryFailure = useCallback((caught: unknown): string => {
    if (isSessionExpired(caught)) {
      redirectToLogin();
      return "";
    }
    return errorMessage(caught, "暂时无法读取，请稍后重试。");
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError("");
    try {
      const catalog = await apiRequest<{ offers: Offer[] }>(
        "/api/catalog?appId=bomana",
      );
      setOffers(catalog.offers);
    } catch (caught) {
      setCatalogError(handleSupplementaryFailure(caught));
    } finally {
      setCatalogLoading(false);
    }
  }, [handleSupplementaryFailure]);

  const loadReferral = useCallback(async () => {
    setReferralLoading(true);
    setReferralLoadError("");
    try {
      const result = await apiRequest<ReferralResponse>("/api/account/referral");
      setReferral(result.referral);
    } catch (caught) {
      setReferralLoadError(handleSupplementaryFailure(caught));
    } finally {
      setReferralLoading(false);
    }
  }, [handleSupplementaryFailure]);

  const loadCommunity = useCallback(async () => {
    setCommunityLoading(true);
    setCommunityError("");
    try {
      const result = await apiRequest<CommunityResponse>("/api/account/community");
      setCommunity(result.community);
    } catch (caught) {
      setCommunityError(handleSupplementaryFailure(caught));
    } finally {
      setCommunityLoading(false);
    }
  }, [handleSupplementaryFailure]);

  const loadAccount = useCallback(async () => {
    setAccountLoading(true);
    setAccountLoadError("");
    try {
      const [sessionResult, accountResult] = await Promise.all([
        apiRequest<SessionResponse>("/api/session"),
        apiRequest<AccountResponse>("/api/account"),
      ]);
      setSession(sessionResult);
      setAccount(accountResult.account);
      void loadCatalog();
      void loadReferral();
      void loadCommunity();
    } catch (caught) {
      if (isSessionExpired(caught)) {
        redirectToLogin();
        return;
      }
      setAccountLoadError(
        errorMessage(caught, "账户信息暂时无法读取，请稍后重试。"),
      );
    } finally {
      setAccountLoading(false);
    }
  }, [loadCatalog, loadCommunity, loadReferral]);

  useEffect(() => {
    setPageMetadata(
      "账户中心 | CheemsPay",
      "管理 CheemsPay 的订单、权益、授权设备、账户安全与支持入口。",
    );
    void loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    const syncActiveSection = () => setActiveSection(readAccountSection());
    window.addEventListener("hashchange", syncActiveSection);
    return () => window.removeEventListener("hashchange", syncActiveSection);
  }, []);

  useEffect(() => {
    const target = document.getElementById(activeSection);
    target?.scrollIntoView({ block: "start" });
    target?.focus({ preventScroll: true });
  }, [activeSection, account !== null]);

  const createOrder = async (
    productId: string,
    pendingRecoveryEmail?: string,
    referralCode?: string,
  ) => {
    setBusyProduct(productId);
    setError("");
    try {
      const order = await createCheemsPayOrder({
        productId,
        refundPolicyAccepted: acceptedRefundPolicies[productId] === true,
        pendingRecoveryEmail,
        referralCode,
      });
      sessionStorage.setItem("cheemspay-last-order", order.orderId);
      window.location.assign(order.paymentUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法创建订单");
      setBusyProduct("");
    }
  };

  const generateReferralCode = async () => {
    setReferralBusy(true);
    setReferralError("");
    try {
      const result = await apiRequest<ReferralResponse>(
        "/api/account/referral/code",
        { method: "POST" },
      );
      setReferral((current) => ({
        ...(current ?? result.referral),
        ...result.referral,
      }));
    } catch (caught) {
      setReferralError(
        caught instanceof Error ? caught.message : "无法生成邀请码",
      );
    } finally {
      setReferralBusy(false);
    }
  };

  const claimReferralCode = async () => {
    const code = referralCodeInput.trim().toUpperCase();
    if (!code) {
      setReferralError("请输入朋友分享的邀请码");
      return;
    }
    setReferralBusy(true);
    setReferralError("");
    setAccountNotice("");
    try {
      await apiRequest("/api/account/referral/claim", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      setReferralCodeInput("");
      await loadReferral();
      setAccountNotice("邀请码已绑定，将在符合条件的首笔年度会员订单中生效。");
    } catch (caught) {
      setReferralError(
        caught instanceof Error ? caught.message : "无法绑定邀请码",
      );
    } finally {
      setReferralBusy(false);
    }
  };

  const copyReferralUrl = async () => {
    if (!referral?.inviteUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(referral.inviteUrl);
      setReferralCopied(true);
    } catch {
      setReferralError("浏览器未允许复制，请手动复制邀请链接。");
    }
  };

  const copyReferralCode = async () => {
    if (!referral?.code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(referral.code);
      setReferralCopied(true);
    } catch {
      setReferralError("浏览器未允许复制，请手动复制邀请码。");
    }
  };

  const copyCommunityAnswer = async () => {
    if (!community?.qqGroupJoinAnswer) {
      return;
    }
    try {
      await navigator.clipboard.writeText(community.qqGroupJoinAnswer);
      setCommunityCopied(true);
    } catch {
      setError("浏览器未允许复制，请手动复制群加群暗号。");
    }
  };

  const copyCommunityGroupNumber = async () => {
    if (!community?.qqGroupNumber) {
      return;
    }
    try {
      await navigator.clipboard.writeText(community.qqGroupNumber);
      setCommunityGroupCopied(true);
    } catch {
      setError("浏览器未允许复制，请手动复制 QQ 群号。");
    }
  };

  const buy = (productId: string) => {
    const offer = offers.find((item) => item.productId === productId);
    if (
      offer &&
      requiresRefundPolicyConsent(offer) &&
      acceptedRefundPolicies[productId] !== true
    ) {
      setError("请先确认该商品的退款政策");
      return;
    }
    if (isFirstPurchaseAccount(account)) {
      setCheckoutProductId(productId);
      setCheckoutEmail("");
      setCheckoutReferralCode("");
      return;
    }
    void createOrder(productId);
  };

  const openRefundDialog = (orderId: string) => {
    setRefundDialogOrderId(orderId);
    setRefundReasonCode("");
    setRefundReasonDetail("");
    setRefundReasonError("");
    setRefundReviewConfirmed(false);
    setError("");
  };

  const closeRefundDialog = () => {
    if (busyRefund) {
      return;
    }
    setRefundDialogOrderId("");
    setRefundReasonCode("");
    setRefundReasonDetail("");
    setRefundReasonError("");
    setRefundReviewConfirmed(false);
  };

  const submitRefund = async (event: FormEvent) => {
    event.preventDefault();
    const option = REFUND_REASON_OPTIONS.find(
      (item) => item.value === refundReasonCode,
    );
    const detail = refundReasonDetail.trim();
    if (!option) {
      setRefundReasonError("请选择退款原因");
      return;
    }
    if (option.value === "OTHER" && detail.length < 3) {
      setRefundReasonError("请补充至少 3 个字的退款说明");
      return;
    }
    if (!refundReviewConfirmed) {
      setRefundReasonError("请确认已了解退款会影响相关权益");
      return;
    }
    const orderId = refundDialogOrderId;
    const reason =
      option.value === "OTHER" ? `其他原因：${detail}` : option.label;
    setBusyRefund(orderId);
    setError("");
    setAccountNotice("");
    setRefundReasonError("");
    try {
      await apiRequest(`/api/orders/${orderId}/refund`, {
        method: "POST",
        body: JSON.stringify({ reasonCode: option.value, reason }),
      });
      setRefundDialogOrderId("");
      setRefundReviewConfirmed(false);
      await loadAccount();
      setAccountNotice("退款申请已提交，当前页面已更新；后续状态会保留在订单记录中。");
    } catch (caught) {
      setRefundReasonError(
        caught instanceof Error ? caught.message : "无法提交退款申请",
      );
    } finally {
      setBusyRefund("");
    }
  };

  const resendVerification = async () => {
    if (!account?.customer?.recoveryEmail) {
      return;
    }
    setError("");
    try {
      await apiRequest("/api/account/recovery-email/resend", {
        method: "POST",
      });
      setVerificationQueued(true);
    } catch (caught) {
      setError(errorMessage(caught, "无法重新发送验证邮件"));
    }
  };

  const bindRecoveryEmail = async (email: string, orderId?: string) => {
    setBindingBusy(true);
    setError("");
    setAccountNotice("");
    try {
      await apiRequest(
        orderId
          ? `/api/orders/${encodeURIComponent(orderId)}/recovery-email`
          : "/api/account/recovery-email",
        {
          method: "POST",
          ...(orderId
            ? {}
            : {
                body: JSON.stringify({ email }),
              }),
        },
      );
      setVerificationQueued(true);
      setBindingEmail("");
      await loadAccount();
      setAccountNotice("验证邮件已发出。完成验证前仍会显示为待验证。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法绑定恢复邮箱");
    } finally {
      setBindingBusy(false);
    }
  };

  const beginTwoFactorSetup = async () => {
    setTwoFactorBusy(true);
    setTwoFactorError("");
    try {
      const setup = await apiRequest<{
        method: "totp";
        totpURI: string;
        backupCodes: string[];
      }>("/api/auth/two-factor/enable", {
        method: "POST",
        body: JSON.stringify({
          password: twoFactorPassword,
          method: "totp",
        }),
      });
      const displayTotpUri = formatTotpEnrollmentUri(
        setup.totpURI,
        session?.user.username ?? session?.user.name,
      );
      const qrCode = await import("qrcode");
      const qrDataUrl = await qrCode.default.toDataURL(displayTotpUri, {
        errorCorrectionLevel: "M",
        margin: 2,
        width: 224,
      });
      setTwoFactorUri(displayTotpUri);
      setTwoFactorQr(qrDataUrl);
      setTwoFactorSecret(readTotpSecret(displayTotpUri));
      setBackupCodes(setup.backupCodes);
      setTwoFactorPassword("");
      setTwoFactorCode("");
    } catch (caught) {
      setTwoFactorError(
        caught instanceof Error ? caught.message : "无法开始设置动态口令",
      );
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const verifyTwoFactorSetup = async () => {
    setTwoFactorBusy(true);
    setTwoFactorError("");
    try {
      await apiRequest("/api/auth/two-factor/verify-totp", {
        method: "POST",
        body: JSON.stringify({
          code: twoFactorCode.trim(),
          trustDevice: false,
        }),
      });
      setSession((current) =>
        current
          ? {
              ...current,
              user: { ...current.user, twoFactorEnabled: true },
            }
          : current,
      );
      setTwoFactorActivated(true);
      setTwoFactorUri("");
      setTwoFactorQr("");
      setTwoFactorSecret("");
      setTwoFactorCode("");
    } catch (caught) {
      setTwoFactorError(
        caught instanceof Error ? caught.message : "动态口令验证失败",
      );
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!twoFactorDisableConfirmed) {
      setTwoFactorError("请确认你了解关闭后将失去当前验证器保护");
      return;
    }
    setTwoFactorBusy(true);
    setTwoFactorError("");
    try {
      await apiRequest<{ status: boolean }>(
        "/api/auth/two-factor/disable",
        {
          method: "POST",
          body: JSON.stringify({ password: twoFactorPassword }),
        },
      );
      setSession((current) =>
        current
          ? {
              ...current,
              user: { ...current.user, twoFactorEnabled: false },
            }
          : current,
      );
      setTwoFactorPassword("");
      setTwoFactorCode("");
      setTwoFactorUri("");
      setTwoFactorQr("");
      setTwoFactorSecret("");
      setBackupCodes([]);
      setTwoFactorActivated(false);
      setBackupCodesCopied(false);
      setTwoFactorDisableConfirmed(false);
    } catch (caught) {
      setTwoFactorError(
        caught instanceof Error ? caught.message : "无法删除验证器",
      );
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setBackupCodesCopied(true);
    } catch {
      setTwoFactorError("浏览器未允许复制，请手动逐项保存备用码。");
    }
  };

  const openDeviceDialog = (deviceId: string) => {
    setDeviceDialogId(deviceId);
    setDeviceError("");
    setDeviceSuccess("");
  };

  const closeDeviceDialog = () => {
    if (deviceBusy) {
      return;
    }
    setDeviceDialogId("");
    setDeviceError("");
  };

  const removeDevice = async () => {
    if (!deviceDialogId) {
      return;
    }
    const deviceId = deviceDialogId;
    setDeviceBusy(true);
    setDeviceError("");
    try {
      await apiRequest(`/api/account/devices/${encodeURIComponent(deviceId)}`, {
        method: "DELETE",
      });
      setAccount((current) =>
        current
          ? {
              ...current,
              devices: current.devices.filter((device) => device.id !== deviceId),
            }
          : current,
      );
      setDeviceDialogId("");
      setDeviceSuccess("设备已解除绑定。");
    } catch (caught) {
      if (isSessionExpired(caught)) {
        redirectToLogin();
        return;
      }
      setDeviceError(errorMessage(caught, "无法解绑该设备，请稍后重试。"));
    } finally {
      setDeviceBusy(false);
    }
  };

  if (!session || !account) {
    return (
      <Shell>
        {accountLoading ? (
          <div className="page-loading">正在读取账户信息…</div>
        ) : (
          <section className="dashboard-card" aria-live="polite">
            <h1>暂时无法打开账户中心</h1>
            {accountLoadError ? (
              <p className="form-error" role="alert">
                {accountLoadError}
              </p>
            ) : null}
            <Button onClick={() => void loadAccount()}>重新加载</Button>
          </section>
        )}
      </Shell>
    );
  }

  const activeEntitlement = account.entitlements
    .filter((item) => item.status === "ACTIVE")
    .sort((left, right) => {
      const leftExpiry = left.expiresAt ? Date.parse(left.expiresAt) : Infinity;
      const rightExpiry = right.expiresAt
        ? Date.parse(right.expiresAt)
        : Infinity;
      return rightExpiry - leftExpiry;
    })[0];
  const pendingCheckoutOrder = account.orders.find(
    (order) =>
      order.orderStatus === "FULFILLED" &&
      order.pendingRecoveryEmail &&
      !order.recoveryEmailActivatedAt,
  );
  const isAdmin = session.user.role
    .split(",")
    .map((role) => role.trim())
    .includes("admin");
  const devicePendingRemoval = account.devices.find(
    (device) => device.id === deviceDialogId,
  );
  const pendingOrderCount = account.orders.filter((order) =>
    [
      "CREATED",
      "PENDING_PAYMENT",
      "PAID",
      "MANUAL_REVIEW",
      "REFUND_REQUESTED",
    ].includes(order.orderStatus),
  ).length;
  const sectionNavItems = [
    { id: "overview", label: "概览", href: "#overview" },
    { id: "orders", label: "订单", badge: pendingOrderCount, href: "#orders" },
    {
      id: "devices",
      label: "设备",
      badge: account.devices.length,
      href: "#devices",
    },
    { id: "security", label: "安全", href: "#security" },
    { id: "support", label: "支持", href: "#support" },
  ] satisfies Array<{ id: AccountSection; label: string; badge?: number; href: string }>;
  return (
    <Shell action={<SignOutControl />}>
      <section className="account-header">
        <div>
          <h1>{session.user.username ?? session.user.name}</h1>
          <p className="account-header-meta">
            <span>{activeEntitlement?.productName ?? "尚未开通"}</span>
            <span>
              恢复：
              {account.customer?.accountRecoveryMode === "EMAIL"
                ? "邮箱"
                : account.customer?.accountRecoveryMode === "EMAIL_PENDING"
                  ? "邮箱待验证"
                  : activeEntitlement
                    ? "历史支付宝身份"
                    : account.customer?.hasRecoverySecret
                      ? "恢复暗号"
                      : "仅账密"}
            </span>
          </p>
        </div>
        <div className="account-header-actions">
          <a className="button button-secondary" href="/account/teams">
            战队
          </a>
          {isAdmin ? (
            <a className="button button-secondary" href="/admin">
              管理
            </a>
          ) : null}
        </div>
      </section>
      {account.customer?.accountRecoveryMode === "EMAIL_PENDING" ||
      (!account.customer?.recoveryEmail && activeEntitlement) ? (
        <div className="account-banner">
          {account.customer?.accountRecoveryMode === "EMAIL_PENDING" ? (
            <>
              <p>
                {account.customer.recoveryEmail} 待验证。
                {verificationQueued ? "验证邮件已发出。" : ""}
              </p>
              <button
                className="inline-action"
                disabled={verificationQueued}
                onClick={() => void resendVerification()}
                type="button"
              >
                {verificationQueued ? "已发送" : "重发验证邮件"}
              </button>
            </>
          ) : pendingCheckoutOrder?.pendingRecoveryEmail ? (
            <button
              className="inline-action"
              disabled={bindingBusy}
              onClick={() =>
                void bindRecoveryEmail(
                  pendingCheckoutOrder.pendingRecoveryEmail!,
                  pendingCheckoutOrder.id,
                )
              }
              type="button"
            >
              {bindingBusy
                ? "正在处理…"
                : `继续绑定 ${pendingCheckoutOrder.pendingRecoveryEmail}`}
            </button>
          ) : (
            <form
              className="email-bind-form"
              onSubmit={(event) => {
                event.preventDefault();
                void bindRecoveryEmail(bindingEmail);
              }}
            >
              <input
                autoComplete="email"
                onChange={(event) => setBindingEmail(event.target.value)}
                placeholder="恢复邮箱"
                required
                type="email"
                value={bindingEmail}
              />
              <button disabled={bindingBusy} type="submit">
                {bindingBusy ? "正在处理…" : "绑定并验证"}
              </button>
            </form>
          )}
        </div>
      ) : null}

      <SectionNav
        activeItem={activeSection}
        items={sectionNavItems}
        label="账户中心分区"
      />

      {accountNotice ? (
        <div className="account-inline-notice form-message" role="status">
          {accountNotice}
        </div>
      ) : null}
      {error ? (
        <div className="account-inline-notice form-error" role="alert">
          {error}
        </div>
      ) : null}

      <section className="account-grid">
        {activeSection === "overview" ? (
          <>
        <article
          aria-labelledby="overview-title"
          className="dashboard-card entitlement-card"
          id="overview"
          tabIndex={-1}
        >
          <div className="card-title-row">
            <div>
              <span className="card-kicker">当前权益</span>
              <h2 id="overview-title">
                {activeEntitlement?.productName ?? "尚未购买"}
              </h2>
            </div>
            <StatusPill tone={activeEntitlement ? "good" : "neutral"}>
              {activeEntitlement ? "有效" : "未生效"}
            </StatusPill>
          </div>
          {activeEntitlement ? (
            <div className="entitlement-expiry">
              <span>服务有效期至</span>
              <strong>
                {activeEntitlement.expiresAt
                  ? new Date(activeEntitlement.expiresAt).toLocaleDateString()
                  : "永久"}
              </strong>
            </div>
          ) : (
            <p className="muted">
              付款确认后，授权会显示在这里。
            </p>
          )}
        </article>

        <RedemptionCodeForm onRedeemed={loadAccount} />

        {activeEntitlement && community?.qqGroupJoinAnswer ? (
          <article className="dashboard-card community-card">
            <div className="card-title-row">
              <div>
                <span className="card-kicker">会员讨论群</span>
                <h2>QQ 加群暗号</h2>
              </div>
              <StatusPill tone="good">会员专属</StatusPill>
            </div>
            <p className="community-intro">
              QQ 群内交流 Bomana 使用问题、更新信息和会员权益。
            </p>
            <div className="community-group-line">
              <span>QQ群号</span>
              <code>{community.qqGroupNumber}</code>
              <Button
                className="secondary"
                onClick={() => void copyCommunityGroupNumber()}
              >
                {communityGroupCopied ? "已复制" : "复制群号"}
              </Button>
            </div>
            <p className="community-answer-label">验证问题答案</p>
            <div className="community-answer-line">
              <code>{community.qqGroupJoinAnswer}</code>
              <Button
                className="secondary"
                onClick={() => void copyCommunityAnswer()}
              >
                {communityCopied ? "已复制" : "复制暗号"}
              </Button>
            </div>
            <small className="community-note">
              仅用于 Bomana 会员讨论和使用帮助，请不要公开转发。
            </small>
            {communityCopied || communityGroupCopied ? (
              <small role="status">已复制到剪贴板。</small>
            ) : null}
          </article>
        ) : null}

        {activeEntitlement && !community && (communityLoading || communityError) ? (
          <article className="dashboard-card community-card">
            <span className="card-kicker">会员讨论群</span>
            <h2>暂时无法读取群信息</h2>
            {communityError ? (
              <p className="form-error" role="alert">
                {communityError}
              </p>
            ) : (
              <p className="muted">正在读取会员讨论群信息…</p>
            )}
            {communityError ? (
              <Button
                className="secondary"
                disabled={communityLoading}
                onClick={() => void loadCommunity()}
              >
                {communityLoading ? "重试中…" : "重新加载"}
              </Button>
            ) : null}
          </article>
        ) : null}

        {referral &&
        (referral.eligible || account.entitlements.length === 0) ? (
          <article className="dashboard-card referral-card">
            <div className="card-title-row">
              <div>
                <span className="card-kicker">推荐奖励</span>
                <h2>{referral.eligible ? "邀请朋友，续费 30 天" : "使用邀请码"}</h2>
              </div>
              {referral.eligible ? (
                <StatusPill tone="good">可参与</StatusPill>
              ) : null}
            </div>
            {referral.eligible ? (
              referral.code ? (
                <div className="referral-share">
                  <p>
                    朋友通过你的链接或邀请码购买 Bomana 1 年会员后，你会自动获得 30 天续费。
                  </p>
                  <div className="referral-code-line">
                    <code>{referral.code}</code>
                    <button
                      className="secondary-button"
                      onClick={() => void copyReferralCode()}
                      type="button"
                    >
                      复制邀请码
                    </button>
                  </div>
                  <div className="referral-link-line">
                    <input readOnly value={referral.inviteUrl ?? ""} />
                    <Button
                      className="secondary"
                      onClick={() => void copyReferralUrl()}
                    >
                      {referralCopied ? "已复制" : "复制链接"}
                    </Button>
                  </div>
                  <small>
                    已成功推荐 {referral.rewardedUsers} 人 · 已绑定 {referral.attributedUsers} 人
                  </small>
                </div>
              ) : (
                <div className="referral-share">
                  <p>激活会员后即可生成一个长期有效的邀请码和邀请链接。</p>
                  <Button
                    disabled={referralBusy}
                    onClick={() => void generateReferralCode()}
                  >
                    {referralBusy ? "生成中…" : "生成我的邀请码"}
                  </Button>
                </div>
              )
            ) : (
              <form
                className="referral-claim"
                onSubmit={(event) => {
                  event.preventDefault();
                  void claimReferralCode();
                }}
              >
                <p>如果朋友分享了邀请码，请先在付款前绑定；首笔年度会员订单才会触发奖励。</p>
                <div className="referral-link-line">
                  <input
                    autoComplete="off"
                    maxLength={16}
                    minLength={8}
                    onChange={(event) =>
                      setReferralCodeInput(
                        event.target.value.toUpperCase().slice(0, 16),
                      )
                    }
                    placeholder="输入 8–16 位邀请码"
                    value={referralCodeInput}
                  />
                  <Button disabled={referralBusy} type="submit">
                    {referralBusy ? "绑定中…" : "绑定邀请码"}
                  </Button>
                </div>
              </form>
            )}
            {referralError ? (
              <div className="form-error" role="alert">
                {referralError}
              </div>
            ) : null}
            {referralCopied ? <small role="status">邀请信息已复制。</small> : null}
          </article>
        ) : null}

        {!referral && (referralLoading || referralLoadError) ? (
          <article className="dashboard-card referral-card">
            <span className="card-kicker">推荐奖励</span>
            <h2>暂时无法读取推荐信息</h2>
            {referralLoadError ? (
              <p className="form-error" role="alert">
                {referralLoadError}
              </p>
            ) : (
              <p className="muted">正在读取推荐奖励信息…</p>
            )}
            {referralLoadError ? (
              <Button
                className="secondary"
                disabled={referralLoading}
                onClick={() => void loadReferral()}
              >
                {referralLoading ? "重试中…" : "重新加载"}
              </Button>
            ) : null}
          </article>
        ) : null}

        <article className="dashboard-card offer-card">
          <span className="card-kicker">续费 / 购买</span>
          {catalogError ? (
            <div>
              <p className="form-error" role="alert">
                商品目录暂时无法读取：{catalogError}
              </p>
              <Button
                className="secondary"
                disabled={catalogLoading}
                onClick={() => void loadCatalog()}
              >
                {catalogLoading ? "重试中…" : "重新加载商品"}
              </Button>
            </div>
          ) : catalogLoading && offers.length === 0 ? (
            <p className="muted">正在读取可购买商品…</p>
          ) : offers.length ? (
            <>
              {offers.map((offer) => (
                <div className="offer-line" key={offer.productId}>
                  <div>
                    <h3>{offer.name}</h3>
                    <p>{offer.description}</p>
                    <small className="offer-rule">
                      {Math.round(offer.durationSeconds / 86_400)} 天 · 最多{" "}
                      {offer.maxDevices} 台设备
                      {offer.purchaseLimit === null
                        ? ""
                        : ` · 每账号限购 ${offer.purchaseLimit} 次`}
                    </small>
                  </div>
                  <div className="offer-buy">
                    {offer.originalAmountFen !== null ? (
                      <del>¥{(offer.originalAmountFen / 100).toFixed(2)}</del>
                    ) : null}
                    <strong>¥{(offer.amountFen / 100).toFixed(2)}</strong>
                    {refundPolicyConsentText(offer) ? (
                      <label className="purchase-refund-consent">
                        <input
                          checked={
                            acceptedRefundPolicies[offer.productId] === true
                          }
                          onChange={(event) => {
                            setAcceptedRefundPolicies((current) => ({
                              ...current,
                              [offer.productId]: event.target.checked,
                            }));
                            setError("");
                          }}
                          type="checkbox"
                        />
                        <span>{refundPolicyConsentText(offer)}</span>
                      </label>
                    ) : null}
                    <Button
                      disabled={
                        busyProduct === offer.productId ||
                        (requiresRefundPolicyConsent(offer) &&
                          acceptedRefundPolicies[offer.productId] !== true)
                      }
                      onClick={() => buy(offer.productId)}
                    >
                      {busyProduct === offer.productId ? "创建中…" : "去支付"}
                    </Button>
                  </div>
                </div>
              ))}
              <p className="refund-policy-note">
                退款规则以订单实付金额为准，提交后由管理员审核。
              </p>
            </>
          ) : (
            <p className="muted">当前没有可购买或续费的商品。</p>
          )}
          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}
        </article>

          </>
        ) : null}

        {activeSection === "security" ? (
          <article
            aria-labelledby="security-title"
            className="dashboard-card span-two security-card"
            id="security"
            tabIndex={-1}
          >
          <div className="card-title-row security-card-heading">
            <div>
              <span className="card-kicker">账户安全</span>
              <h2 id="security-title">验证器动态口令</h2>
            </div>
            <StatusPill
              tone={session.user.twoFactorEnabled ? "good" : "neutral"}
            >
              {session.user.twoFactorEnabled ? "已开启" : "未开启"}
            </StatusPill>
          </div>

          {session.user.twoFactorEnabled ? (
            <div className="security-active">
              <div>
                <strong>登录保护已生效</strong>
                <p>
                  登录时除密码外，还需要输入验证器中的动态口令。
                </p>
              </div>
              {twoFactorActivated && backupCodes.length ? (
                <section
                  aria-labelledby="backup-codes-title"
                  className="backup-codes-panel"
                >
                  <div>
                    <span className="card-kicker">仅本次显示</span>
                    <h3 id="backup-codes-title">立即保存备用码</h3>
                    <p>
                      每个备用码只能使用一次。若手机丢失，它们是重新登录的重要凭据。
                    </p>
                  </div>
                  <ul>
                    {backupCodes.map((code) => (
                      <li key={code}>
                        <code>{code}</code>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="secondary"
                    onClick={() => void copyBackupCodes()}
                    type="button"
                  >
                    {backupCodesCopied ? "已复制" : "复制全部备用码"}
                  </Button>
                </section>
              ) : null}
              <div className="security-start security-disable">
                <div>
                  <strong>不再使用验证器？</strong>
                  <p>
                    删除后会同时清除当前 TOTP 密钥和备用码，账密登录不再需要动态口令。
                    操作需要重新输入当前密码。
                  </p>
                </div>
                <form
                  className="security-start-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void disableTwoFactor();
                  }}
                >
                  <Field
                    label="确认当前密码"
                    hint="这是敏感操作，需要重新验证密码"
                  >
                    <input
                      autoComplete="current-password"
                      maxLength={128}
                      minLength={10}
                      onChange={(event) =>
                        setTwoFactorPassword(event.target.value)
                      }
                      placeholder="输入当前登录密码"
                      required
                      type="password"
                      value={twoFactorPassword}
                    />
                  </Field>
                  <label>
                    <input
                      checked={twoFactorDisableConfirmed}
                      disabled={twoFactorBusy}
                      onChange={(event) => {
                        setTwoFactorDisableConfirmed(event.target.checked);
                        setTwoFactorError("");
                      }}
                      type="checkbox"
                    />
                    我了解关闭后，当前验证器与备用码将失效，登录不再要求动态口令。
                  </label>
                  {twoFactorError ? (
                    <div className="form-error" role="alert">
                      {twoFactorError}
                    </div>
                  ) : null}
                  <Button
                    className="secondary"
                    disabled={twoFactorBusy || !twoFactorDisableConfirmed}
                    type="submit"
                  >
                    {twoFactorBusy ? "正在删除…" : "删除验证器"}
                  </Button>
                </form>
              </div>
            </div>
          ) : twoFactorUri ? (
            <div className="totp-setup">
              <div className="totp-setup-visual">
                <img
                  alt="CheemsPay 动态口令设置二维码"
                  height="224"
                  src={twoFactorQr}
                  width="224"
                />
                <small>二维码在本机生成，密钥不会上传。</small>
              </div>
              <div className="totp-setup-copy">
                <span className="card-kicker">第 2 步，共 2 步</span>
                <h3>扫码，再输入动态口令</h3>
                <ol>
                  <li>用任意 TOTP 验证器 App 扫描左侧二维码。</li>
                  <li>输入 App 当前显示的 6 位数字完成激活。</li>
                </ol>
                {twoFactorSecret ? (
                  <div className="totp-manual-secret">
                    <span>无法扫码时手动输入密钥</span>
                    <code>
                      {twoFactorSecret.match(/.{1,4}/g)?.join(" ") ??
                        twoFactorSecret}
                    </code>
                    <small>类型 TOTP · 6 位 · 每 30 秒更新</small>
                  </div>
                ) : null}
                <form
                  className="totp-verify-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void verifyTwoFactorSetup();
                  }}
                >
                  <Field
                    label="6 位动态口令"
                    hint="若即将更新，可等待下一组数字再输入"
                  >
                    <input
                      autoComplete="one-time-code"
                      autoFocus
                      inputMode="numeric"
                      maxLength={6}
                      minLength={6}
                      onChange={(event) =>
                        setTwoFactorCode(
                          event.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      pattern="[0-9]{6}"
                      placeholder="000000"
                      required
                      value={twoFactorCode}
                    />
                  </Field>
                  {twoFactorError ? (
                    <div className="form-error" role="alert">
                      {twoFactorError}
                    </div>
                  ) : null}
                  <Button disabled={twoFactorBusy} type="submit">
                    {twoFactorBusy ? "正在验证…" : "验证并开启"}
                  </Button>
                  <button
                    className="inline-action"
                    disabled={twoFactorBusy}
                    onClick={() => {
                      setTwoFactorUri("");
                      setTwoFactorQr("");
                      setTwoFactorSecret("");
                      setBackupCodes([]);
                      setTwoFactorError("");
                    }}
                    type="button"
                  >
                    取消并重新开始
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="security-start">
              <div>
                <strong>可选开启，不需要绑定邮箱</strong>
                <p>
                  普通用户可以继续只用密码登录；管理员必须开启动态口令。
                </p>
              </div>
              <form
                className="security-start-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void beginTwoFactorSetup();
                }}
              >
                <Field
                  label="先确认当前密码"
                  hint="这是敏感操作，需要重新验证密码"
                >
                  <input
                    autoComplete="current-password"
                    maxLength={128}
                    minLength={10}
                    onChange={(event) =>
                      setTwoFactorPassword(event.target.value)
                    }
                    placeholder="输入当前登录密码"
                    required
                    type="password"
                    value={twoFactorPassword}
                  />
                </Field>
                {twoFactorError ? (
                  <div className="form-error" role="alert">
                    {twoFactorError}
                  </div>
                ) : null}
                <Button disabled={twoFactorBusy} type="submit">
                  {twoFactorBusy ? "正在生成…" : "开始设置"}
                </Button>
              </form>
            </div>
          )}
        </article>

        ) : null}

        {activeSection === "orders" ? (
          <article
            aria-labelledby="orders-title"
            className="dashboard-card span-two"
            id="orders"
            tabIndex={-1}
          >
          <div className="card-title-row">
            <div>
              <span className="card-kicker">订单</span>
              <h2 id="orders-title">购买记录</h2>
            </div>
          </div>
          {account.orders.length ? (
            <div className="data-list">
              {account.orders.map((order) => (
                <div className="data-row" key={order.id}>
                  <div>
                    <strong>{order.productName}</strong>
                    <small>{order.outTradeNo}</small>
                    {order.refundReason ? (
                      <small className="order-refund-reason">
                        退款理由：{order.refundReason}
                      </small>
                    ) : null}
                  </div>
                  <span>¥{(order.amountFen / 100).toFixed(2)}</span>
                  <StatusPill
                    tone={order.orderStatus === "FULFILLED" ? "good" : "warn"}
                  >
                    {ORDER_STATUS_LABELS[order.orderStatus] ?? order.orderStatus}
                  </StatusPill>
                  <div className="order-meta">
                    <time>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </time>
                    {isRefundRequestAvailable(order) ? (
                      <button
                        disabled={busyRefund === order.id}
                        onClick={() => openRefundDialog(order.id)}
                        type="button"
                      >
                        申请退款
                      </button>
                    ) : order.orderStatus === "FULFILLED" &&
                      order.refundPolicyCode === "NON_REFUNDABLE" ? (
                      <small>该订单不支持退款</small>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="暂无订单">购买后会显示在这里。</EmptyState>
          )}
        </article>

        ) : null}

        {activeSection === "devices" ? (
          <article
            aria-labelledby="devices-title"
            className="dashboard-card"
            id="devices"
            tabIndex={-1}
          >
          <span className="card-kicker">设备</span>
          <h2 id="devices-title">已授权设备</h2>
          {deviceSuccess ? (
            <p className="muted" role="status">
              {deviceSuccess}
            </p>
          ) : null}
          {account.devices.length ? (
            <div className="compact-list">
              {account.devices.map((device) => (
                <div key={device.id}>
                  <span>
                    <strong>{device.name}</strong>
                    <small>{device.appId}</small>
                  </span>
                  <button
                    onClick={() => openDeviceDialog(device.id)}
                    type="button"
                  >
                    解绑
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">尚无桌面设备绑定。</p>
          )}
        </article>

        ) : null}

        {activeSection === "overview" ? (
          <article className="dashboard-card">
          <span className="card-kicker">消息</span>
          <h2>通知</h2>
          {account.messages.length ? (
            <div className="compact-list messages">
              {account.messages.slice(0, 4).map((message) => (
                <div key={message.id}>
                  <span>
                    <strong>{message.title}</strong>
                    <small>{message.body}</small>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">重要通知会出现在这里。</p>
          )}
          </article>
        ) : null}

        {activeSection === "support" ? (
          <div aria-label="客服支持" id="support" tabIndex={-1}>
            <SupportSummary />
          </div>
        ) : null}
      </section>
      <Dialog
        description={
          devicePendingRemoval
            ? `将撤销“${devicePendingRemoval.name}”的当前授权。该设备下次使用时需要重新登录并授权。`
            : undefined
        }
        dismissible={!deviceBusy}
        footer={
          <>
            <Button disabled={deviceBusy} onClick={() => void removeDevice()}>
              {deviceBusy ? "正在解绑…" : "确认解绑"}
            </Button>
            <button
              className="secondary-button"
              disabled={deviceBusy}
              onClick={closeDeviceDialog}
              type="button"
            >
              取消
            </button>
          </>
        }
        onClose={closeDeviceDialog}
        open={Boolean(devicePendingRemoval)}
        title="解除设备绑定？"
      >
        {deviceError ? (
          <div className="form-error" role="alert">
            {deviceError}
          </div>
        ) : null}
      </Dialog>
      <Dialog
        description="低价商品 14 天内退 50%；¥18.80 起按有效使用次数分档。故障与重复付款单独审核。"
        dismissible={busyRefund !== refundDialogOrderId}
        footer={
          <>
            <Button
              disabled={
                busyRefund === refundDialogOrderId || !refundReviewConfirmed
              }
              form="refund-request-form"
              type="submit"
            >
              {busyRefund === refundDialogOrderId ? "提交中…" : "提交退款申请"}
            </Button>
            <button
              className="secondary-button"
              disabled={busyRefund === refundDialogOrderId}
              onClick={closeRefundDialog}
              type="button"
            >
              取消
            </button>
          </>
        }
        onClose={closeRefundDialog}
        open={Boolean(refundDialogOrderId)}
        title="为什么需要退款？"
      >
        <form
          className="refund-dialog"
          id="refund-request-form"
          onSubmit={(event) => void submitRefund(event)}
        >
          <fieldset className="refund-reason-fieldset">
            <legend>退款原因</legend>
            <div className="refund-reason-list">
              {REFUND_REASON_OPTIONS.map((option) => (
                <label
                  className={`refund-reason-option${
                    refundReasonCode === option.value ? " selected" : ""
                  }`}
                  key={option.value}
                >
                  <input
                    checked={refundReasonCode === option.value}
                    name="refund-reason"
                    onChange={() => {
                      setRefundReasonCode(option.value);
                      setRefundReasonError("");
                    }}
                    type="radio"
                    value={option.value}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {refundReasonCode === "OTHER" ? (
            <Field label="补充说明" hint="至少 3 个字，最多 500 个字">
              <input
                autoFocus
                maxLength={500}
                onChange={(event) => {
                  setRefundReasonDetail(event.target.value);
                  setRefundReasonError("");
                }}
                placeholder="请说明退款原因"
                required
                value={refundReasonDetail}
              />
            </Field>
          ) : null}
          <label>
            <input
              checked={refundReviewConfirmed}
              onChange={(event) => {
                setRefundReviewConfirmed(event.target.checked);
                setRefundReasonError("");
              }}
              type="checkbox"
            />
            我已了解：提交后需等待人工审核；退款结果会重新计算相关权益。
          </label>
          {refundReasonError ? (
            <div className="form-error" role="alert">
              {refundReasonError}
            </div>
          ) : null}
        </form>
      </Dialog>
      <RecoveryEmailCheckoutDialog
        busy={busyProduct === checkoutProductId}
        email={checkoutEmail}
        error={error}
        onClose={() => {
          if (busyProduct !== checkoutProductId) {
            setCheckoutProductId("");
          }
        }}
        onEmailChange={setCheckoutEmail}
        onReferralChange={setCheckoutReferralCode}
        onSkip={() =>
          void createOrder(checkoutProductId, undefined, checkoutReferralCode)
        }
        onSubmit={() =>
          void createOrder(
            checkoutProductId,
            checkoutEmail,
            checkoutReferralCode,
          )
        }
        open={Boolean(checkoutProductId)}
        referralCode={checkoutReferralCode}
      />
    </Shell>
  );
}
